import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Legally Binding Signatures',
      description: 'ESIGN Act and UETA compliant. Full audit trail with IP address, timestamp, and device info. Holds up in court.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Mobile-First Customer Form',
      description: 'Customers check in on their phone. QR code at the counter, text them a link, or use a tablet. Their choice.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Real-Time Dashboard',
      description: 'See check-ins the instant they happen. Customer info, vehicle details, service request - all in one place.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
        </svg>
      ),
      title: 'One-Click Copy',
      description: 'Copy customer info, vehicle details, or everything at once. Paste directly into Tekmetric, Shop-Ware, Mitchell, or any system.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: 'Clean RO Numbers',
      description: 'No more $0 ROs cluttering your reports. Get authorization BEFORE you start the repair order.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Complete Customer Data',
      description: 'Name, phone, email, full address, vehicle year/make/model, VIN, plate, and service request. Everything you need.'
    }
  ];

  const painPoints = [
    'Creating $0 ROs just to get authorization to touch a vehicle',
    'Chasing customers around the lot with a clipboard',
    'Illegible handwriting on paper forms',
    'Missing signatures holding up the workflow',
    'No proof of authorization when disputes arise',
    'Manually typing customer info into your shop system'
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <span className="text-2xl text-amber-400">*</span>
              <span className="text-xl font-bold text-white">Authorization Hook</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-slate-300 hover:text-white transition">Features</a>
              <a href="#pricing" className="text-slate-300 hover:text-white transition">Pricing</a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white transition">How It Works</a>
              <button 
                onClick={() => navigate('/login')}
                className="text-slate-300 hover:text-white transition"
              >
                Login
              </button>
              <button 
                onClick={() => navigate('/signup')}
                className="px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition"
              >
                Get Started
              </button>
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-300 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-800">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-slate-300 hover:text-white transition">Features</a>
                <a href="#pricing" className="text-slate-300 hover:text-white transition">Pricing</a>
                <a href="#how-it-works" className="text-slate-300 hover:text-white transition">How It Works</a>
                <button 
                  onClick={() => navigate('/login')}
                  className="text-slate-300 hover:text-white transition text-left"
                >
                  Login
                </button>
                <button 
                  onClick={() => navigate('/signup')}
                  className="px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition w-full"
                >
                  Get Started
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Stop Creating <span className="text-amber-400">$0 ROs</span><br />
            Just to Get a Signature
          </h1>
          <p className="text-xl sm:text-2xl text-slate-400 max-w-3xl mx-auto mb-10">
            Authorization Hook handles the intake <span className="text-white font-medium">before</span> the RO. 
            Digital signatures, customer info, vehicle details — all ready to paste into your shop system.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => navigate('/signup')}
              className="px-8 py-4 bg-amber-500 text-slate-900 font-bold text-lg rounded-xl hover:bg-amber-400 transition shadow-lg shadow-amber-500/25 w-full sm:w-auto"
            >
              Get Started — $50/mo
            </button>
            <a 
              href="#how-it-works"
              className="px-8 py-4 bg-slate-800 text-white font-semibold text-lg rounded-xl hover:bg-slate-700 transition w-full sm:w-auto"
            >
              See How It Works
            </a>
          </div>
          <p className="mt-6 text-slate-500 text-sm">No contracts. Cancel anytime. Get started in 5 minutes.</p>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 px-4 bg-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4">
            Sound Familiar?
          </h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
            Every shop management system focuses on what happens after the RO. 
            Nobody solves the initial intake problem.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {painPoints.map((point, i) => (
              <div key={i} className="flex items-start space-x-3 bg-slate-800 rounded-xl p-4">
                <div className="flex-shrink-0 w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <span className="text-slate-300">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
            Everything You Need. Nothing You Don't.
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Built by a shop owner who was tired of the same problems you face every day.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="bg-slate-800 rounded-2xl p-6 hover:bg-slate-750 transition">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
            Up and Running in 5 Minutes
          </h2>
          <p className="text-slate-400 text-center mb-12">
            No complicated setup. No IT department required.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Sign Up',
                description: 'Create your account, add your shop name and contact info. That\'s it.'
              },
              {
                step: '2',
                title: 'Share Your Link',
                description: 'Get your unique check-in URL. Print a QR code, text it to customers, or use a tablet at the counter.'
              },
              {
                step: '3',
                title: 'Watch Intakes Roll In',
                description: 'Customers fill out the form, sign digitally, and you see it instantly on your dashboard. Copy and paste into your shop system.'
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center text-slate-900 text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
            Simple Pricing. No Surprises.
          </h2>
          <p className="text-slate-400 text-center mb-12">
            One plan. Everything included. No per-intake fees.
          </p>
          
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-3xl p-8 sm:p-12 border border-slate-700 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-sm font-bold px-4 py-1 rounded-bl-xl">
              UNLIMITED INTAKES
            </div>
            
            <div className="text-center mb-8">
              <div className="flex items-center justify-center">
                <span className="text-5xl sm:text-6xl font-bold text-white">$50</span>
                <span className="text-slate-400 text-xl ml-2">/month</span>
              </div>
              <p className="text-slate-400 mt-2">per shop location</p>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                'Unlimited customer intakes',
                'Unlimited advisor accounts',
                'Legally binding digital signatures',
                'Full audit trail on every intake',
                'Real-time dashboard',
                'One-click copy to any shop system',
                'Mobile-friendly customer form',
                'QR code for counter display',
                'Email support'
              ].map((item, i) => (
                <li key={i} className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-300">{item}</span>
                </li>
              ))}
            </ul>

            <button 
              onClick={() => navigate('/signup')}
              className="w-full py-4 bg-amber-500 text-slate-900 font-bold text-lg rounded-xl hover:bg-amber-400 transition"
            >
              Get Started Now
            </button>
            
            <p className="text-center text-slate-500 text-sm mt-4">
              No contracts. No setup fees. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Ready to Fix Your Intake Process?
          </h2>
          <p className="text-xl text-slate-800 mb-8">
            Join shops that stopped creating $0 ROs and started getting signatures the smart way.
          </p>
          <button 
            onClick={() => navigate('/signup')}
            className="px-8 py-4 bg-slate-900 text-white font-bold text-lg rounded-xl hover:bg-slate-800 transition shadow-lg"
          >
            Get Started — $50/mo
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <span className="text-2xl text-amber-400">*</span>
              <span className="text-xl font-bold text-white">Authorization Hook</span>
            </div>
            <div className="flex items-center space-x-6 text-slate-400 text-sm">
              <a href="/privacy" className="hover:text-white transition">Privacy Policy</a>
              <a href="/terms" className="hover:text-white transition">Terms of Service</a>
              <a href="mailto:support@auth-hook.com" className="hover:text-white transition">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Authorization Hook. Built by shop owners, for shop owners.</p>
            <p className="mt-2 text-slate-600">All uploaded images (license plates, VINs) are retained for 30 days then automatically deleted.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
